// MOTO (mail order / telephone order) card registration via Stripe confirmation tokens.
// Used when createSetupIntent returns useMoto=true (operator + AB test; see shouldUseMoto on backend).
//
// Flow: createSetupIntent (SetupIntent or registration PaymentIntent) → token → check → addPaymentSource (confirm + save/subscribe)

export type MotoBillingDetails = {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postal_code: string;
    state: string;
  };
};

export type MotoConfirmSetupResult = {
  setupIntentId: string;
  confirmationTokenId?: string;
  error?: { code: string; message: string; decline_code?: string };
};

export type MotoStripeContext = {
  stripe: any;
  elements: any;
  setupIntentId: string;
};

export const motoStripeService = {
  /**
   * MOTO card capture: Elements submit → confirmation token.
   * setupIntentId is the Stripe intent id (seti_* or pi_*).
   */
  confirmMotoSetup: async (context: MotoStripeContext, billingDetails?: MotoBillingDetails): Promise<MotoConfirmSetupResult> => {
    const { stripe, elements, setupIntentId } = context;

    const { error: submitError } = await elements.submit();
    if (submitError) {
      return {
        setupIntentId,
        error: {
          code: submitError.code,
          message: submitError.message,
          decline_code: submitError.decline_code,
        },
      };
    }

    const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({
      elements,
      params: {
        payment_method_data: {
          billing_details: billingDetails,
        },
      },
    });

    if (tokenError) {
      return {
        setupIntentId,
        error: {
          code: tokenError.code,
          message: tokenError.message,
          decline_code: tokenError.decline_code,
        },
      };
    }

    return {
      setupIntentId,
      confirmationTokenId: confirmationToken.id,
    };
  },
};
